import { useEffect, useMemo, useState } from 'react';
import { TrendingDown, Gauge, Link2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RR_THRESHOLD = -15;
const eur = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} €`);
const pct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)} %`);
const color = (n) => (n == null ? 'text-slate-400' : n < 0 ? 'text-red-600' : 'text-emerald-600');

// Référence du run-rate : prévi saisie > CA de la simulation liée > CA N-1 ramené en HT.
const analyse = (c, runRateAnnual, link) => {
  const rr = runRateAnnual(c);
  const simCa = link?.sim_ca || null;
  const ref = c.previ_ca_annuel_ht || simCa || (c.ca_n1 ? c.ca_n1 / 1.1 : null);
  const refType = c.previ_ca_annuel_ht ? 'Prévi saisie' : simCa ? 'Simulation' : c.ca_n1 ? 'CA N-1 HT' : null;
  const rrDelta = rr != null && ref ? (rr - ref) / ref * 100 : null;
  const negTrend = c.trend != null && c.trend < 0;
  const rrLow = rrDelta != null && rrDelta <= RR_THRESHOLD;
  return { rr, ref, refType, rrDelta, negTrend, rrLow };
};

export const ParcAnalysisTab = ({ corners, runRateAnnual, onRowClick, token, canEdit, updateCornerField }) => {
  const [mode, setMode] = useState('all');
  const [links, setLinks] = useState({});
  const [simOptions, setSimOptions] = useState([]);
  const [linking, setLinking] = useState(null);
  const loadLinks = () => fetch(`${BACKEND_URL}/api/ca-corner/sim-links`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => (r.ok ? r.json() : null)).then(d => { if (d) { setLinks(d.links || {}); setSimOptions(d.simulations || []); } }).catch(() => {});
  useEffect(() => { loadLinks(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const linkSim = async (cornerId, simulationId) => {
    setLinking(null);
    await fetch(`${BACKEND_URL}/api/ca-corner/corners/${cornerId}/simulation`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ simulation_id: simulationId || null }),
    });
    loadLinks();
  };
  const allRows = useMemo(() => corners
    .filter(c => c.has_ca !== false && c.ca_n_current)
    .map(c => ({ c, a: analyse(c, runRateAnnual, links[c.id]) })), [corners, runRateAnnual, links]);
  const rows = allRows.filter(({ a }) => a.negTrend || a.rrLow);
  const shown = (mode === 'parc' ? allRows : rows)
    .filter(({ a }) => mode === 'all' || mode === 'parc' || (mode === 'trend' ? a.negTrend : a.rrLow))
    .sort((x, y) => (Math.min(x.c.trend ?? 0, x.a.rrDelta ?? 0) - Math.min(y.c.trend ?? 0, y.a.rrDelta ?? 0)));
  const chips = [
    ['all', `Tous (${rows.length})`],
    ['trend', `Évolution négative vs N-1 (${rows.filter(r => r.a.negTrend).length})`],
    ['rr', `Run-rate ≤ ${RR_THRESHOLD} % vs prévi (${rows.filter(r => r.a.rrLow).length})`],
    ['parc', `Tout le parc — saisir les prévis (${allRows.length})`],
  ];

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="parc-analysis-tab">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 space-y-2">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-600" />Analyse du parc : évolutions négatives et run-rate hors prévi</p>
        <p className="text-xs text-slate-500">Run-rate comparé, dans l'ordre : à la prévi saisie, sinon au CA prévisionnel de la simulation CAPEX liée (Direction > commercial > outil), sinon au CA N-1 ramené en HT. Classement du plus fort recul au plus faible.</p>
        <div className="flex flex-wrap gap-2">
          {chips.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`parc-filter-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${mode === k ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-400">
            <tr>{['Corner', 'Compte SF', 'DR', 'Mois', 'CA HT cumul N', 'Tendance vs N-1', 'Run-rate annuel HT', 'Prévi saisie', 'CA simulation', 'Référence', 'Écart run-rate', 'Alertes'].map(h => <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map(({ c, a }) => (
              <tr key={c.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-orange-50/40 cursor-pointer" onClick={() => onRowClick(c.id)} data-testid={`parc-row-${c.id}`}>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{c.name_powerbi}</td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{c.name_sf || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{c.nom_dr || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{c.ca_n_month_label || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{eur(c.ca_n_current)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap ${color(c.trend)}`}>{pct(c.trend)}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{eur(a.rr)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <input type="number" min="0" step="1000" defaultValue={c.previ_ca_annuel_ht ?? ''} key={`pp-${c.id}-${c.previ_ca_annuel_ht ?? ''}`} disabled={!canEdit}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    onBlur={(e) => { const v = e.target.value; if (String(v) !== String(c.previ_ca_annuel_ht ?? '')) updateCornerField(c.id, { previ_ca_annuel_ht: v === '' ? null : v }); }}
                    placeholder="—" className="w-28 px-2 py-1 text-right rounded border border-slate-200 bg-white dark:bg-slate-800 disabled:opacity-60" data-testid={`parc-previ-${c.id}`} />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  {linking === c.id ? (
                    <select autoFocus defaultValue={links[c.id]?.simulation_id || ''} onChange={e => linkSim(c.id, e.target.value)} onBlur={() => setLinking(null)}
                      className="max-w-[220px] h-8 rounded border border-slate-200 text-xs bg-white dark:bg-slate-800" data-testid={`parc-sim-select-${c.id}`}>
                      <option value="">— Aucune simulation —</option>
                      {simOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  ) : (
                    <button type="button" disabled={!canEdit} onClick={() => setLinking(c.id)} data-testid={`parc-sim-link-${c.id}`}
                      title={links[c.id] ? `${links[c.id].project_name} (${links[c.id].source === 'auto' ? 'liée automatiquement' : 'liée manuellement'})` : 'Lier une simulation'}
                      className="inline-flex items-center gap-1 text-right tabular-nums disabled:cursor-default">
                      {links[c.id] ? <>{eur(links[c.id].sim_ca)}<Link2 className={`w-3 h-3 ${links[c.id].source === 'auto' ? 'text-slate-400' : 'text-orange-600'}`} /></>
                        : canEdit ? <span className="text-xs text-orange-700 hover:underline">Lier</span> : '—'}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{eur(a.ref)}<span className="block text-[10px] text-slate-400">{a.refType || ''}</span></td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap ${color(a.rrDelta)}`}>{pct(a.rrDelta)}</td>
                <td className="px-3 py-2 whitespace-nowrap space-x-1">
                  {a.negTrend && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 px-2 py-0.5 text-[10px] font-bold"><TrendingDown className="w-3 h-3" />Évolution</span>}
                  {a.rrLow && <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5 text-[10px] font-bold"><Gauge className="w-3 h-3" />Run-rate</span>}
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">Aucun corner concerné.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
